import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconArrowLeft } from '@sorare/core/src/atoms/icons/system/iconArrowLeft.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_SERIES_TASK_LINEUPS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { ThresholdStep } from '#src/components/pro/ThresholdStep/index.tsx';
import { HotStreakTitle } from '#src/components/series/HotStreakTitle/index.tsx';

import type {
  HotStreaksTutorialStoryQuery,
  HotStreaksTutorialStoryQueryVariables,
  HotStreaksTutorialStory_tasksTrack,
} from './__generated__/index.graphql.ts';

const HOT_STREAKS_TUTORIAL_STORY_QUERY = gql`
  query HotStreaksTutorialStoryQuery($taskId: ID!) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        target
        ... on ThresholdPickerTask {
          id
          averageMode
          myFilteredBench(
            filters: { sortType: { direction: DESC, type: POPULARITY } }
            first: 5
          ) {
            nodes {
              id
              pictureUrl
              anyPlayer {
                slug
                activeClub {
                  slug
                }
                activeNationalTeam {
                  slug
                }
              }
              eligiblePlayerGameScores(taskId: $taskId) {
                id
                anyGame {
                  id
                  date
                  awayTeam {
                    slug
                    pictureUrl
                  }
                  homeTeam {
                    slug
                    pictureUrl
                  }
                }
              }
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  HotStreaksTutorialStoryQuery,
  HotStreaksTutorialStoryQueryVariables
>;

const dialog = css`
  overflow: hidden;

  @media ${laptopAndAbove} {
    align-self: center;
    height: auto;
    max-height: min(90vh, calc(90 * var(--unit)));
  }
`;

const stepLayout = css`
  position: relative;
  isolation: isolate;
  height: 100%;
  min-height: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: calc(8 * var(--unit)) 0 0;
`;

const Step = ({ children }: { children: ReactNode }) => (
  <Vertical gap={3} className={stepLayout}>
    <SecondaryPageBackground
      overlayStartColor="var(--c-surface-light)"
      overlayMidColor="var(--c-score-low)"
    />
    {children}
  </Vertical>
);

const CardImage = styled.img`
  width: 100%;
`;

const TeamLogo = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  border-radius: 50%;
`;

const CardWrapper = styled(Vertical)`
  flex: 1;
  min-width: 80px;
  &:first-child,
  &:last-child {
    opacity: 0.4;
  }

  &:nth-child(2),
  &:nth-child(4) {
    min-width: 110px;
  }

  &:nth-child(3) {
    min-width: 140px;
  }
`;

const CardsRow = styled(Horizontal)`
  margin-block: auto;
  overflow: hidden;
  width: 100%;
`;

/**
 * The tutorial previews the ladder before any step is won, so the lowest step
 * is presented as the one to aim for.
 */
const REACHED_LEVEL = 1;

const HotStreaksTutorialContent = ({
  task,
}: {
  task: Extract<
    NonNullable<HotStreaksTutorialStoryQuery['currentUser']>['task'],
    { __typename: 'ThresholdPickerTask' }
  >;
}) => {
  const { myFilteredBench } = task;
  const { nodes } = myFilteredBench;

  return (
    <Vertical gap={6} grow>
      <Vertical center gap={2} className="px-2 text-center">
        <HotStreakTitle />
        <BodyL color="var(--c-black)">
          <FormattedMessage
            defaultMessage="Score more than {points} PTS in <b>upcoming matches.</b>"
            values={{
              points: <span>{task.target}</span>,
              b: chunks => (
                <BodyL as="span" bold>
                  {chunks}
                </BodyL>
              ),
            }}
          />
        </BodyL>
      </Vertical>
      <CardsRow gap={1} center>
        {nodes.map(card => {
          const game = card.eligiblePlayerGameScores?.at(0)?.anyGame;
          const playerTeamSlugs = [
            card.anyPlayer.activeClub?.slug,
            card.anyPlayer.activeNationalTeam?.slug,
          ].filter(Boolean);
          const teamPictureUrl = playerTeamSlugs.some(
            slug => slug === game?.homeTeam?.slug
          )
            ? game?.awayTeam?.pictureUrl
            : game?.homeTeam?.pictureUrl;
          return (
            <CardWrapper key={card.id} center gap={1}>
              <BrandXS color="var(--c-black)">
                <FormattedDate
                  value={game?.date}
                  weekday="short"
                  day="numeric"
                />
              </BrandXS>
              <CardImage src={card.pictureUrl || ''} alt="" />
              <Horizontal gap={1} center>
                <BrandS color="var(--c-nd-600)">
                  <FormattedMessage {...fantasy.vs} />
                </BrandS>
                <TeamLogo src={teamPictureUrl || ''} alt="" />
              </Horizontal>
            </CardWrapper>
          );
        })}
      </CardsRow>
    </Vertical>
  );
};

type HotStreaksTrackContentProps = {
  tasksTrack: HotStreaksTutorialStory_tasksTrack;
};

const HotStreaksTrackContent = ({
  tasksTrack,
}: HotStreaksTrackContentProps) => {
  const { steps } = tasksTrack;
  const descendingSteps = [...steps].reverse();

  return (
    <Vertical gap={4} grow className="px-2">
      <Vertical center gap={2} className="text-center">
        <HotStreakTitle />
        <BodyL color="var(--c-black)">
          <FormattedMessage
            defaultMessage="Advance your streak of wins{br}to earn bigger rewards!"
            values={{ br: <br /> }}
          />
        </BodyL>
      </Vertical>
      <Vertical gap={2.5} center>
        {descendingSteps.map((step, index) => {
          const level = descendingSteps.length - index;
          const leadsIntoReachedStep = level - 1 <= REACHED_LEVEL - 1;

          return (
            <ThresholdStep
              key={step.id}
              level={level}
              target={step.target}
              locked={level > REACHED_LEVEL}
              active={level === REACHED_LEVEL}
              animationOrder={descendingSteps.length - 1 - index}
              rewardConfigs={step.rewardConfigs}
              tailColor={leadsIntoReachedStep ? undefined : 'var(--c-nl-500)'}
              activeColor="var(--c-score-low)"
              rewardTextColor="var(--c-white)"
              dimmed={level !== REACHED_LEVEL}
            />
          );
        })}
      </Vertical>
    </Vertical>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  taskTrack: Nullable<HotStreaksTutorialStory_tasksTrack>;
};

export const HotStreaksTutorialStory = ({
  open,
  onClose,
  taskTrack,
}: Props) => {
  const navigate = useNavigate();
  const { update: updateLifecycle } = useLifecycle();
  const { generateSportContextPath } = useSportContext();
  const [isFirstStep, setIsFirstStep] = useState(true);

  const taskId = taskTrack?.steps?.at(0)?.id;

  const { data } = useQuery(HOT_STREAKS_TUTORIAL_STORY_QUERY, {
    variables: { taskId: idFromObject(taskId) || '' },
    skip: !taskId,
  });
  const task = isType(data?.currentUser?.task, 'ThresholdPickerTask')
    ? data.currentUser.task
    : undefined;

  if (!task || !taskTrack) {
    return null;
  }

  const markOnboardingSeen = () => {
    updateLifecycle(LIFECYCLE.sawSeriesHotStreakOnboarding, true);
  };

  const handleClose = () => {
    markOnboardingSeen();
    setIsFirstStep(true);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      hideHeader
      className={dialog}
    >
      <Step>
        <div className="absolute top-2 left-2 z-10">
          <Button
            size="medium"
            startIcon={<FontAwesomeIcon icon={iconArrowLeft} />}
            color="tertiary"
            onClick={handleClose}
          />
        </div>
        <Vertical
          grow
          className="min-h-0 min-w-0 overflow-x-hidden overflow-y-auto"
        >
          {isFirstStep ? (
            <HotStreaksTutorialContent task={task} />
          ) : (
            <HotStreaksTrackContent tasksTrack={taskTrack} />
          )}
        </Vertical>
        <Horizontal center className="px-2 pb-2">
          {isFirstStep ? (
            <Button
              size="medium"
              disableDebounce
              color="secondary"
              onClick={() => setIsFirstStep(false)}
            >
              <FormattedMessage {...glossary.continue} />
            </Button>
          ) : (
            <Button
              size="medium"
              disableDebounce
              color="primary"
              onClick={() => {
                markOnboardingSeen();
                navigate(
                  generateSportContextPath(ANY_SPORT_SERIES_TASK_LINEUPS, {
                    params: { task: idFromObject(task.id) },
                  })
                );
              }}
            >
              <FormattedMessage defaultMessage="Build my lineup" />
            </Button>
          )}
        </Horizontal>
      </Step>
    </Dialog>
  );
};

HotStreaksTutorialStory.fragments = {
  tasksTrack: gql`
    fragment HotStreaksTutorialStory_tasksTrack on TasksTrack {
      id
      steps {
        id
        target
        rewardConfigs {
          id
          ...ThresholdStep_rewardConfig
        }
      }
    }
    ${ThresholdStep.fragments.rewardConfig}
  ` as TypedDocumentNode<HotStreaksTutorialStory_tasksTrack>,
};
